import type {
  ChunkId,
  GeoBounds,
  LocalBounds2,
  LocalMetricFrame,
  LocalPoint2,
  MacrotileId,
  OsmSourceProvenance,
} from "../../world-model/src/index.ts";

export const CHUNK_FORMAT = "daylight-club.world-chunk" as const;
export const CHUNK_SCHEMA_VERSION = 1 as const;

export type ChunkIndexArray = Uint16Array | Uint32Array;

export interface LocalBounds3 extends LocalBounds2 {
  readonly minY: number;
  readonly maxY: number;
}

export const MESH_KINDS = [
  "terrain",
  "road",
  "junction",
  "marking",
  "kerb",
  "sidewalk",
  "dirt-path",
  "bridge-deck",
  "bridge-barrier",
  "bridge-support",
  "tunnel-shell",
  "tunnel-portal",
  "retaining-wall",
  "debug",
] as const;

export type MeshKind = (typeof MESH_KINDS)[number];
export type MeshTopology = "triangles" | "lines";

export const PROCEDURAL_MATERIALS = [
  "terrain-grass",
  "terrain-rock",
  "asphalt",
  "concrete",
  "paving",
  "cobblestone",
  "compacted",
  "gravel",
  "dirt",
  "sand",
  "grass",
  "wood",
  "metal",
  "road-marking-white",
  "road-marking-yellow",
  "kerb",
  "bridge-structure",
  "tunnel-interior",
  "debug",
] as const;

export type ProceduralMaterialId = (typeof PROCEDURAL_MATERIALS)[number];

/** GPU-ready geometry. Every position uses the chunk header's z14 solve frame. */
export interface CompiledMesh {
  readonly id: string;
  readonly kind: MeshKind;
  readonly material: ProceduralMaterialId;
  readonly topology: MeshTopology;
  readonly bounds: LocalBounds3;
  /** XYZ, X east/Y up/Z south. */
  readonly positions: Float32Array;
  readonly normals?: Float32Array;
  readonly uvs?: Float32Array;
  readonly colors?: Uint8Array;
  readonly indices: ChunkIndexArray;
}

export type ColliderMaterial =
  | "terrain"
  | "asphalt"
  | "concrete"
  | "loose"
  | "wood"
  | "metal";

export interface TriangleMeshCollider {
  readonly id: string;
  readonly kind: "trimesh";
  readonly material: ColliderMaterial;
  /** XYZ in the z14 solve frame. */
  readonly positions: Float32Array;
  readonly indices: ChunkIndexArray;
}

export interface HeightfieldCollider {
  readonly id: string;
  readonly kind: "heightfield";
  readonly material: ColliderMaterial;
  readonly origin: LocalPoint2;
  readonly columns: number;
  readonly rows: number;
  readonly stepX: number;
  readonly stepZ: number;
  readonly heights: Float32Array;
}

export type CompiledCollider = TriangleMeshCollider | HeightfieldCollider;

/** Runtime terrain sampling grid; rows advance north-to-south (+Z). */
export interface TerrainSampleGrid {
  readonly origin: LocalPoint2;
  readonly columns: number;
  readonly rows: number;
  readonly stepX: number;
  readonly stepZ: number;
  readonly rowOrder: "north-to-south";
  readonly heights: Float32Array;
  /** Optional 0/1 mask with one byte per height sample. */
  readonly validity?: Uint8Array;
}

export const ROAD_SAMPLE_FLAGS = {
  bridge: 1 << 0,
  tunnel: 1 << 1,
  junction: 1 << 2,
  oneway: 1 << 3,
  path: 1 << 4,
} as const;

/**
 * Packed runtime road samples. Sample i reads XYZ at i*3, tangent XZ at i*2,
 * width/roadIndex/flags at i. `roadIndex` addresses `roadIds`.
 */
export interface RoadSampleTable {
  readonly roadIds: readonly string[];
  readonly positions: Float32Array;
  readonly tangents: Float32Array;
  readonly widths: Float32Array;
  readonly roadIndex: Uint32Array;
  readonly flags: Uint16Array;
}

/** Compact graph for spawning, route hints, and the minimap. */
export interface CompiledRoadGraph {
  readonly nodeIds: readonly string[];
  readonly nodePositions: Float32Array;
  /** Node-index pairs. */
  readonly edgeNodeIndices: Uint32Array;
  /** One roadIds index per edge. */
  readonly edgeRoadIndex: Uint32Array;
}

export type ChunkSeamSide = "north" | "east" | "south" | "west";

/**
 * Border contract generated from canonical boundary samples. Road crossings are
 * [offsetAlongEdge, elevation, width, tangentX, tangentZ] records.
 */
export interface ChunkSeamEdge {
  readonly side: ChunkSeamSide;
  readonly signature: string;
  readonly terrainHeights: Float32Array;
  readonly roadIds: readonly string[];
  readonly roadCrossings: Float32Array;
}

export interface ChunkSeamData {
  readonly north: ChunkSeamEdge;
  readonly east: ChunkSeamEdge;
  readonly south: ChunkSeamEdge;
  readonly west: ChunkSeamEdge;
}

export interface ChunkCompilerIdentity {
  readonly name: string;
  readonly version: string;
  readonly rulesVersion: number;
}

export interface ChunkElevationProvenance {
  readonly provider: string;
  readonly dataset?: string;
  readonly revision?: string;
  readonly verticalDatum?: string;
}

export interface ChunkProvenance {
  readonly osm: readonly OsmSourceProvenance[];
  readonly elevation: readonly ChunkElevationProvenance[];
}

export interface ChunkDiagnostics {
  readonly sourceRoadCount: number;
  readonly solvedRoadCount: number;
  readonly triangleCount: number;
  readonly warningCodes: readonly string[];
}

/**
 * The only world payload consumed by the runtime. It is plain structured-clone
 * metadata plus transferable typed arrays; no Three.js, Rapier, or DOM objects.
 */
export interface CompiledWorldChunk {
  readonly format: typeof CHUNK_FORMAT;
  readonly schemaVersion: typeof CHUNK_SCHEMA_VERSION;
  readonly compiler: ChunkCompilerIdentity;
  readonly id: ChunkId;
  readonly macrotile: MacrotileId;
  /** Shared by every z17 child emitted from this z14 solve. */
  readonly frame: LocalMetricFrame;
  readonly geoBounds: GeoBounds;
  readonly localBounds: LocalBounds3;
  /** Deterministic hash of canonical content, excluding diagnostics. */
  readonly contentHash: string;
  readonly meshes: readonly CompiledMesh[];
  readonly colliders: readonly CompiledCollider[];
  readonly terrain: TerrainSampleGrid;
  readonly roads: RoadSampleTable;
  readonly roadGraph: CompiledRoadGraph;
  readonly seams: ChunkSeamData;
  readonly provenance: ChunkProvenance;
  readonly diagnostics?: ChunkDiagnostics;
}

export interface ChunkTransferMessage {
  readonly type: "compiled-world-chunk";
  readonly schemaVersion: typeof CHUNK_SCHEMA_VERSION;
  readonly chunk: CompiledWorldChunk;
}
